import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, BellRing } from "lucide-react";
import EmptyState from "./EmptyState";
import Pagination from "./Pagination";
import { fmtDate } from "../utils/inventory.helpers";

const PAGE_SIZE = 12;

function normalizeAlertType(alert) {
  const raw = String(
    alert?.alert_type ||
      alert?.alertType ||
      alert?.type ||
      ""
  )
    .trim()
    .toLowerCase();

  if (!raw) return "OTHER";

  if (
    raw === "low_stock" ||
    raw === "low stock" ||
    raw === "lowstock" ||
    raw === "low" ||
    raw === "stock_low"
  ) {
    return "LOW_STOCK";
  }

  if (
    raw === "expiry_soon" ||
    raw === "expiry soon" ||
    raw === "expiring" ||
    raw === "expiry" ||
    raw === "expiring_soon" ||
    raw === "near_expiry"
  ) {
    return "EXPIRY_SOON";
  }

  if (raw === "expired") return "EXPIRED";

  return raw.toUpperCase();
}

function getAlertBadgeClass(type) {
  if (type === "LOW_STOCK") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  if (type === "EXPIRY_SOON") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }

  if (type === "EXPIRED") {
    return "border-slate-200 bg-slate-50 text-slate-700";
  }

  return "border-slate-200 bg-slate-50 text-slate-700";
}

function getAlertLabel(type) {
  if (type === "LOW_STOCK") return "LOW STOCK";
  if (type === "EXPIRY_SOON") return "EXPIRING";
  if (type === "EXPIRED") return "EXPIRED";
  return type.replaceAll("_", " ");
}

function getField(alert, keys, fallback = "") {
  for (const key of keys) {
    if (
      alert?.[key] !== undefined &&
      alert?.[key] !== null &&
      alert?.[key] !== ""
    ) {
      return alert[key];
    }
  }
  return fallback;
}

function getAlertSubtitle(type, alert) {
  if (type === "LOW_STOCK") {
    const available = Number(
      getField(alert, ["qty_available", "qtyAvailable"], 0)
    );
    const threshold = Number(
      getField(alert, ["threshold", "low_stock_threshold", "lowStockThreshold"], 0)
    );

    return `Available Qty: ${available}${threshold ? ` | Threshold: ${threshold}` : ""}`;
  }

  if (type === "EXPIRY_SOON" || type === "EXPIRED") {
    const expiryDate = getField(alert, ["expiry_date", "expiryDate"]);
    const batchNo = getField(alert, ["batch_no", "batchNo"]);
    const qtyAvailable = Number(
      getField(alert, ["qty_available", "qtyAvailable"], 0)
    );

    return [
      expiryDate ? `Expiry: ${fmtDate(expiryDate)}` : null,
      batchNo ? `Batch: ${batchNo}` : null,
      qtyAvailable || qtyAvailable === 0 ? `Qty: ${qtyAvailable}` : null,
    ]
      .filter(Boolean)
      .join(" | ");
  }

  return "Inventory alert";
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
      <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-3 text-[28px] font-bold text-slate-800">
        {value}
      </div>
    </div>
  );
}

function FilterButton({ active, children, onClick, activeClass = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-semibold transition",
        active
          ? activeClass || "border-[#d86a78] bg-[#fff6f7] text-[#d86a78]"
          : "border-[#f2c6cc] bg-white text-slate-600 hover:bg-[#fff8f9]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function StockAlertsSection({
  alerts,
  loading,
  alertsPage,
  alertsTotalPages,
  setAlertsPage,
}) {
  const [filterType, setFilterType] = useState("ALL");

  const normalizedAlerts = useMemo(() => {
    return (alerts || []).map((item) => ({
      ...item,
      __normalizedType: normalizeAlertType(item),
    }));
  }, [alerts]);

  const filteredAlerts = useMemo(() => {
    if (filterType === "ALL") return normalizedAlerts;
    return normalizedAlerts.filter((item) => item.__normalizedType === filterType);
  }, [normalizedAlerts, filterType]);

  const computedTotalPages = Math.max(
    1,
    Math.ceil(filteredAlerts.length / PAGE_SIZE)
  );

  const safePage = Math.min(alertsPage, computedTotalPages);

  const paginatedAlerts = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredAlerts.slice(start, start + PAGE_SIZE);
  }, [filteredAlerts, safePage]);

  useEffect(() => {
    if (alertsPage > computedTotalPages) {
      setAlertsPage(computedTotalPages);
    }
  }, [alertsPage, computedTotalPages, setAlertsPage]);

  const totalAlerts = normalizedAlerts.length;
  const lowStockCount = normalizedAlerts.filter(
    (item) => item.__normalizedType === "LOW_STOCK"
  ).length;
  const expirySoonCount = normalizedAlerts.filter(
    (item) =>
      item.__normalizedType === "EXPIRY_SOON" ||
      item.__normalizedType === "EXPIRED"
  ).length;

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <StatCard label="Total Alerts" value={totalAlerts} />
        <StatCard label="Low Stock Alerts" value={lowStockCount} />
        <StatCard label="Expiry Alerts" value={expirySoonCount} />
      </div>

      <div className="rounded-[20px] border border-[#f2c6cc] bg-white p-5">
        <div className="flex flex-wrap items-center gap-3">
          <FilterButton
            active={filterType === "ALL"}
            onClick={() => {
              setFilterType("ALL");
              setAlertsPage(1);
            }}
          >
            All Alerts
          </FilterButton>

          <FilterButton
            active={filterType === "LOW_STOCK"}
            activeClass="border-amber-300 bg-amber-50 text-amber-700"
            onClick={() => {
              setFilterType("LOW_STOCK");
              setAlertsPage(1);
            }}
          >
            Low Stock
          </FilterButton>

          <FilterButton
            active={filterType === "EXPIRY_SOON"}
            activeClass="border-rose-300 bg-rose-50 text-rose-700"
            onClick={() => {
              setFilterType("EXPIRY_SOON");
              setAlertsPage(1);
            }}
          >
            Expiry Soon
          </FilterButton>
        </div>
      </div>

      <div className="rounded-[20px] border border-[#f2c6cc] bg-white">
        <div className="flex items-center gap-2 border-b border-[#f7d9de] px-5 py-4">
          <AlertTriangle size={18} className="text-[#d86a78]" />
          <h3 className="text-[16px] font-bold text-slate-800">Stock Alerts</h3>
          <span className="ml-auto rounded-full bg-[#fff6f7] px-3 py-1 text-[12px] font-semibold text-[#d86a78]">
            {filteredAlerts.length} records
          </span>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-[14px] text-slate-500">
            Loading alerts...
          </div>
        ) : paginatedAlerts.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[1050px] w-full border-separate border-spacing-0">
                <thead>
                  <tr className="bg-[#fff8f9]">
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Product
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      SKU
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      UOM
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Batch
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Qty
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Expiry
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Type
                    </th>
                    <th className="border-b border-[#f7d9de] px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
                      Details
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedAlerts.map((alert, index) => {
                    const alertType = alert.__normalizedType;

                    const productName = getField(
                      alert,
                      ["name", "product_name", "productName"],
                      "Product"
                    );

                    const sku = getField(alert, ["sku"], "-");
                    const uom = getField(alert, ["uom", "unit_symbol", "unitSymbol"], "-");
                    const batchNo = getField(alert, ["batch_no", "batchNo"], "-");
                    const qtyAvailable = getField(
                      alert,
                      ["qty_available", "qtyAvailable"],
                      "-"
                    );
                    const expiryDate = getField(alert, ["expiry_date", "expiryDate"], "");

                    return (
                      <tr
                        key={alert.id || `${alertType}-${index}`}
                        className="transition hover:bg-[#fffafb]"
                      >
                        <td className="border-b border-[#f9e5e8] px-4 py-3">
                          <div className="flex items-center gap-2">
                            <BellRing size={15} className="shrink-0 text-[#d86a78]" />
                            <div className="max-w-[260px] truncate text-[13px] font-semibold text-slate-800">
                              {productName}
                            </div>
                          </div>
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3 text-[13px] text-slate-600">
                          {sku}
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3 text-[13px] text-slate-600">
                          {uom}
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3 text-[13px] text-slate-600">
                          {batchNo}
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3 text-[13px] font-semibold text-slate-700">
                          {qtyAvailable}
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3 text-[13px] text-slate-600">
                          {expiryDate ? fmtDate(expiryDate) : "-"}
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3">
                          <span
                            className={[
                              "inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                              getAlertBadgeClass(alertType),
                            ].join(" ")}
                          >
                            {getAlertLabel(alertType)}
                          </span>
                        </td>

                        <td className="border-b border-[#f9e5e8] px-4 py-3 text-[12px] text-slate-500">
                          {getAlertSubtitle(alertType, alert)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4">
              <Pagination
                page={safePage}
                totalPages={computedTotalPages || alertsTotalPages || 1}
                onChange={setAlertsPage}
              />
            </div>
          </>
        ) : (
          <div className="p-6">
            <EmptyState text="No alerts found" />
          </div>
        )}
      </div>
    </>
  );
}